import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { AlertTriangle } from 'lucide-react';

// Inconsistencias — onde o Est. Atual (Geral) nao bate com a soma das prateleiras ([DA-339]).
//
// TRES ABAS, e a separacao e o ponto ([DA-360], 2026-07-23):
//
// 1. Divergencia — a fila que se resolve. Dois achados na MESMA lista:
//    a) divergencia propriamente dita: so aparece produto com TODAS as prateleiras contadas; com uma
//       `—` no meio a soma nao e menor, e desconhecida. Diferenca POSITIVA = o total diz ter mais do
//       que as prateleiras somam (furto, quebra, saida sem passar pelo caixa); NEGATIVA = entrada
//       nao lancada. Os dois sao achado, nao erro de sistema — e o produto do modulo.
//    b) estoque NEGATIVO ([DA-329]), no total ou numa prateleira. A venda parou de bloquear por
//       falta de estoque e o negativo virou a evidencia; a coluna Motivo separa os casos.
// 2. Sem endereco — produto que nao tem prateleira nenhuma. A cascata e inerte nele: o total sobe e
//    desce e nao ha lugar pra acompanhar.
// 3. Sem contagem — tem prateleira, mas alguma esta em `—`. Idem: fica fora da cascata e da aba 1.
//
// 4. Prateleira nao cadastrada — o texto de localizacao GRAVADO na ficha nao e codigo nenhum do
//    cadastro. E o unico lugar do sistema onde "prateleira que nao existe" pode existir: o endereco
//    novo tem FK e nao aceita valor solto, entao pelas outras abas a resposta e zero por construcao.
//    Aberta a pedido do Ruan (07/08) depois de duas caçadas a prateleiras que, no fim, existiam.
//
// So a ABA 1 alimenta a faixa de alerta global ([DA-341]) — em duas faixas, uma por achado. Ela vale
// porque APAGA quando esta tudo certo: foi assim que as 3 linhas da nota da Martins apareceram. As
// abas 2 e 3 sao 633 linhas fixas de faxina de fundo (2026-07-23); na mesma contagem, o alarme
// ficaria aceso pra sempre e mudo na pratica. A aba 4 e o caso extremo disso: a coluna esta
// CONGELADA, entao ela nao diminui nem quando alguem arruma o produto.

type Aba = 'divergencia' | 'sem-endereco' | 'sem-contagem' | 'texto-livre';

// `soma_prateleiras` e `diferenca` sao NULL quando a soma e DESCONHECIDA (produto sem prateleira, ou
// com alguma em `—`) — caso que so existe desde que o negativo entrou nesta lista. NULL nao e zero:
// tratar como zero mostraria "0,00" e afirmaria que as prateleiras somam nada.
interface Linha {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  status: string;
  estoque_atual: string;
  soma_prateleiras: string | null;
  diferenca: string | null;
  lugares: number;
  lugares_negativos: number;
  menor_prateleira: string | null;
  detalhe: string | null;
}

interface LinhaPendencia {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  status: string;
  estoque_atual: string;
  lugares: number;
  contados: number;
  ultima_entrada: string | null;
  detalhe: string | null;
}

/** Aba 4: so o que a lista mostra. Sem foto/estoque de proposito — o Ruan pediu 3 colunas. */
interface LinhaTexto {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  status: string;
  gravado: string;
}

interface Contadores {
  divergencia: number;
  sem_endereco: number;
  sem_contagem: number;
  texto_livre: number;
}

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v: unknown) => String(num(v)).replace('.', ',');
/** Soma desconhecida vira `—`, nunca `0` — ver o comentario da interface `Linha`. */
const fmtOuTraco = (v: string | null) => (v === null || v === undefined ? '—' : fmt(v));

// `ultima_entrada` vem de uma coluna `date` (data pura, sem hora e sem fuso). Parsear com `new Date()`
// jogaria pro fuso local e mostraria o dia anterior — por isso o split na mao.
function fmtData(d: string | null): string {
  if (!d) return '—';
  const [a, m, dia] = d.slice(0, 10).split('-');
  return a && m && dia ? `${dia}/${m}/${a}` : '—';
}

/** Mercadoria que chegou nos ultimos 90 dias: e o caso que doi, e o que ordena a fila. */
function entradaRecente(d: string | null): boolean {
  if (!d) return false;
  const limite = new Date();
  limite.setDate(limite.getDate() - 90);
  return d.slice(0, 10) >= limite.toISOString().slice(0, 10);
}

const ALERTA = { background: '#f97316', color: '#000', fontWeight: 700, borderRadius: 3, padding: '1px 6px' } as const;

const ABAS: Aba[] = ['divergencia', 'sem-endereco', 'sem-contagem', 'texto-livre'];

export function Inconsistencias() {
  const navigate = useNavigate();
  const location = useLocation();
  const [aba, setAba] = useState<Aba>('divergencia');
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [pendencias, setPendencias] = useState<LinhaPendencia[]>([]);
  const [textoLivre, setTextoLivre] = useState<LinhaTexto[]>([]);
  const [contadores, setContadores] = useState<Contadores>({ divergencia: 0, sem_endereco: 0, sem_contagem: 0, texto_livre: 0 });
  const [carregando, setCarregando] = useState(true);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const qs = mostrarInativos ? '?incluir_inativos=true' : '';

  // A faixa de alerta manda em qual aba abrir. OBEDECE e CONSOME ([DA-420]): o state gruda no
  // history e sobrevive ao F5, entao sem o `state: null` toda atualizacao de tela voltaria pra esta
  // aba pra sempre, ignorando a que o operador escolheu a mao. Vale `location.key` como dep porque e
  // ele que muda a cada navegacao — inclusive a de consumo, que so re-roda pra nao achar nada.
  useEffect(() => {
    const alvo = (location.state as { aba?: string } | null)?.aba;
    if (!alvo || !ABAS.includes(alvo as Aba)) return;
    setAba(alvo as Aba);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    let cancelado = false;
    api.get<Contadores>(`/api/localizacoes/pendencias-contadores${qs}`)
      .then((r) => { if (!cancelado) setContadores(r); })
      .catch(() => { /* contador e enfeite: falhou, as abas so ficam sem numero */ });
    return () => { cancelado = true; };
  }, [qs]);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    const url = aba === 'divergencia'
      ? `/api/localizacoes/inconsistencias${qs}`
      : aba === 'texto-livre'
        ? `/api/localizacoes/pendencias-texto-livre${qs}`
        : `/api/localizacoes/pendencias-endereco${qs ? qs + '&' : '?'}tipo=${aba}`;

    api.get<Linha[] | LinhaPendencia[] | LinhaTexto[]>(url)
      .then((r) => {
        if (cancelado) return;
        // Zerar as outras duas listas em toda troca de aba: sem isso a lista antiga fica no estado e
        // o grid segue navegando por itens que a tela nao mostra mais.
        setLinhas(aba === 'divergencia' ? (r as Linha[]) : []);
        setTextoLivre(aba === 'texto-livre' ? (r as LinhaTexto[]) : []);
        setPendencias(aba === 'divergencia' || aba === 'texto-livre' ? [] : (r as LinhaPendencia[]));
      })
      .catch(() => { if (!cancelado) { setLinhas([]); setPendencias([]); setTextoLivre([]); } })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [aba, qs]);

  const itensAtivos: Array<{ produto_id: number }> =
    aba === 'divergencia' ? linhas : aba === 'texto-livre' ? textoLivre : pendencias;
  const grid = useGridNavegavel<{ produto_id: number }>({
    itens: itensAtivos,
    getId: (l) => l.produto_id,
    onAtivar: (l) => navigate(`/produtos/${l.produto_id}`),
  });

  const explicacao = aba === 'divergencia'
    ? <>Produtos em que o <strong>Est. Atual (Geral)</strong> nao bate com a soma das prateleiras, mais
        os que estao com estoque <strong>abaixo de zero</strong> (no total ou numa prateleira). Na
        divergencia so entram os que tem <strong>todas</strong> as prateleiras contadas — onde ainda ha
        <strong> —</strong>, a soma e desconhecida, nao errada. A coluna <strong>Motivo</strong> diz o que
        cada linha e. Enter ou duplo-clique abre a ficha.</>
    : aba === 'sem-endereco'
      ? <>Produtos <strong>sem prateleira nenhuma</strong>. O estoque sobe e desce e nao ha lugar pra
          acompanhar — a mercadoria entra e ninguem sabe onde ela esta. Os que <strong>receberam nota
          ha pouco</strong> vem primeiro: sao os que doem. Enter ou duplo-clique abre a ficha.</>
      : aba === 'texto-livre'
        ? <>Produtos cuja <strong>localizacao gravada na ficha</strong> nao e nenhum codigo do cadastro
            de Localizacoes. E o texto livre antigo, <strong>congelado</strong> na importacao de 17/07
            — nada mais escreve nele, e por isso ele e o unico lugar que pode guardar prateleira sem
            cadastro. A maioria e o mesmo lugar escrito de outro jeito (<strong>G 15</strong> = G15,
            <strong> B3</strong> = B03) ou dois lugares numa anotacao so (<strong>G10 C01</strong>).
            Enter ou duplo-clique abre a ficha.</>
        : <>Produtos com prateleira cadastrada mas <strong>alguma ainda em —</strong> (ninguem contou).
            Enquanto houver um <strong>—</strong>, o produto fica fora da cascata e fora da aba de
            divergencia. Enter ou duplo-clique abre a ficha.</>;

  const vazio = aba === 'divergencia'
    ? 'Nenhuma divergencia entre o total e as prateleiras contadas, e nenhum estoque negativo.'
    : aba === 'sem-endereco'
      ? 'Todo produto tem ao menos uma prateleira.'
      : aba === 'texto-livre'
        ? 'Nenhum produto com localizacao gravada fora do cadastro.'
        : 'Nenhuma prateleira pendente de contagem.';

  const rotuloTotal = aba === 'divergencia'
    ? 'fora de esquadro'
    : aba === 'sem-endereco'
      ? 'sem endereco'
      : aba === 'texto-livre'
        ? 'com prateleira nao cadastrada'
        : 'com prateleira sem contar';

  const total = itensAtivos.length;

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <AlertTriangle size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} /> Inconsistencias de Estoque
        </h2>
      </div>

      <div className="abas">
        <button type="button" className={`aba-btn ${aba === 'divergencia' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('divergencia')}>
          Divergencia ({contadores.divergencia})
        </button>
        <button type="button" className={`aba-btn ${aba === 'sem-endereco' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('sem-endereco')}>
          Sem endereco ({contadores.sem_endereco})
        </button>
        <button type="button" className={`aba-btn ${aba === 'sem-contagem' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('sem-contagem')}>
          Sem contagem ({contadores.sem_contagem})
        </button>
        <button type="button" className={`aba-btn ${aba === 'texto-livre' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('texto-livre')}>
          Prateleira nao cadastrada ({contadores.texto_livre})
        </button>
      </div>

      <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>{explicacao}</p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
          <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
          Mostrar inativos
        </label>
        <span className="muted" style={{ fontSize: 13, marginLeft: 'auto' }}>
          {total > 0 ? (
            <span style={ALERTA}>{total} produto(s) {rotuloTotal}</span>
          ) : 'nada pendente'}
        </span>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : total === 0 ? (
        <p className="muted">{vazio}</p>
      ) : aba === 'divergencia' ? (
        <table className="tabela">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Foto</th>
              <th style={{ width: 80 }}>Codigo</th>
              <th>Descricao</th>
              <th>Prateleiras</th>
              <th style={{ width: 100, textAlign: 'right' }}>Soma</th>
              <th style={{ width: 100, textAlign: 'right' }}>Est. Atual (Geral)</th>
              <th style={{ width: 110, textAlign: 'right' }}>Diferenca</th>
              <th style={{ width: 150 }}>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              // Diferenca ZERO nao e diferenca. Uma linha que entrou so pelo negativo pode ter as
              // prateleiras fechando exato com o total (-5 e -5), e ai o selo laranja com "0"
              // afirmaria uma divergencia que nao existe. Mesmo limiar do backend.
              const temDif = l.diferenca !== null && Math.abs(num(l.diferenca)) > 0.001;
              const dif = num(l.diferenca);
              const totalNegativo = num(l.estoque_atual) < 0;
              const prateleiraNegativa = l.lugares_negativos > 0;
              // A linha pode estar aqui por DOIS motivos independentes, e o que fazer muda conforme
              // o caso — por isso a coluna, e nao so um selo generico. Negativo no total costuma ser
              // nota nao lancada; negativo SO na prateleira e a cascata drenando a ultima da fila
              // ([DA-339]), mercadoria que saiu de um lugar onde nao havia.
              const motivo = totalNegativo && prateleiraNegativa
                ? 'Negativo: total e prateleira'
                : totalNegativo ? 'Negativo: total'
                : prateleiraNegativa ? 'Negativo: prateleira'
                : 'Divergencia';
              const motivoTitulo = totalNegativo
                ? 'Estoque do produto abaixo de zero — costuma ser nota de entrada nao lancada'
                : prateleiraNegativa
                  ? `Prateleira abaixo de zero (menor: ${fmtOuTraco(l.menor_prateleira)}) — saiu mercadoria de um lugar onde nao havia`
                  : 'O total nao bate com a soma das prateleiras contadas';
              return (
                <tr key={l.produto_id} {...grid.rowProps(l)}>
                  <td>
                    {l.foto_url
                      ? <img src={l.foto_url} alt="" className="produto-thumb" loading="lazy" />
                      : <div className="produto-thumb-vazio" />}
                  </td>
                  <td className="text-gray-500 font-bold no-underline">{l.codigo_interno}</td>
                  <td>
                    {l.descricao}
                    {l.status !== 'ativo' && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>(inativo)</span>}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{l.detalhe ?? '—'}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{fmtOuTraco(l.soma_prateleiras)}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {totalNegativo ? <span style={ALERTA}>{fmt(l.estoque_atual)}</span> : fmt(l.estoque_atual)}
                  </td>
                  {/* Cor NAO decide sozinha: o sinal ja diz a direcao, e vermelho/verde e indistinguivel
                      pro Ruan. Fundo solido laranja + preto, o padrao de alerta da casa.
                      Sem diferenca = soma desconhecida (linha que entrou so pelo negativo) — `—`, nao 0. */}
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {temDif ? (
                      <span
                        title={dif > 0
                          ? 'O total diz ter MAIS do que as prateleiras somam — saida sem passar pelo caixa (furto, quebra, cortesia)'
                          : 'As prateleiras somam MAIS que o total — costuma ser entrada nao lancada'}
                        style={ALERTA}
                      >
                        {dif > 0 ? '+' : ''}{fmt(l.diferenca)}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {motivo === 'Divergencia'
                      ? <span className="muted" title={motivoTitulo}>{motivo}</span>
                      : <span style={ALERTA} title={motivoTitulo}>{motivo}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : aba === 'texto-livre' ? (
        // TRES colunas, sem foto e sem estoque — pedido do Ruan. A pergunta desta aba e "o que esta
        // escrito aqui?"; qualquer coluna a mais e ruido pra ela.
        <table className="tabela">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Codigo</th>
              <th>Descricao</th>
              <th style={{ width: 200 }}>Gravado na ficha</th>
            </tr>
          </thead>
          <tbody>
            {textoLivre.map((l) => (
              <tr key={l.produto_id} {...grid.rowProps(l)}>
                <td className="text-gray-500 font-bold no-underline">{l.codigo_interno}</td>
                <td>
                  {l.descricao}
                  {l.status !== 'ativo' && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>(inativo)</span>}
                </td>
                {/* Mono + `pre-wrap` porque aqui o ESPACO e o dado: o que separa "G 15" de "G15" e a
                    unica coisa que explica a linha estar nesta aba. Em fonte proporcional o HTML
                    ainda colapsaria os espacos repetidos de "G 14   F5" e a evidencia sumiria. */}
                <td className="cel-num" style={{ whiteSpace: 'pre-wrap' }}>{l.gravado}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="tabela">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Foto</th>
              <th style={{ width: 80 }}>Codigo</th>
              <th>Descricao</th>
              {aba === 'sem-contagem' && <th>Prateleiras</th>}
              <th style={{ width: 110, textAlign: 'right' }}>Est. Atual (Geral)</th>
              <th style={{ width: 140, textAlign: 'right' }}>Ultima entrada</th>
            </tr>
          </thead>
          <tbody>
            {pendencias.map((l) => (
              <tr key={l.produto_id} {...grid.rowProps(l)}>
                <td>
                  {l.foto_url
                    ? <img src={l.foto_url} alt="" className="produto-thumb" loading="lazy" />
                    : <div className="produto-thumb-vazio" />}
                </td>
                <td className="text-gray-500 font-bold no-underline">{l.codigo_interno}</td>
                <td>
                  {l.descricao}
                  {l.status !== 'ativo' && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>(inativo)</span>}
                </td>
                {aba === 'sem-contagem' && (
                  <td className="muted" style={{ fontSize: 12 }}>
                    {l.detalhe} <span style={{ marginLeft: 4 }}>({l.contados} de {l.lugares} contadas)</span>
                  </td>
                )}
                <td className="cel-num" style={{ textAlign: 'right' }}>{fmt(l.estoque_atual)}</td>
                {/* Realce so na entrada RECENTE: sem ele a lista e um bloco de 465 linhas iguais e a
                    urgencia some. Laranja solido + preto, mesmo par de alerta da aba 1. */}
                <td style={{ textAlign: 'right' }}>
                  {entradaRecente(l.ultima_entrada) ? (
                    <span style={ALERTA} title="Recebeu mercadoria nos ultimos 90 dias — entrou estoque sem ter onde guardar">
                      {fmtData(l.ultima_entrada)}
                    </span>
                  ) : (
                    <span className="muted">{fmtData(l.ultima_entrada)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
